import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, format, addDays } from "date-fns";
import { LEAGUES_CONFIG } from "@/components/filters/LeagueFilter";
import { WORLD_CUP_API_LEAGUE_ID, WORLD_CUP_OFFICIAL_LOGO, isWorldCupWindow } from "@/lib/worldCup2026";

/** Returns the official WC emblem for league_api_id=1, otherwise the original logo. */
function resolveLeagueLogo(apiId: number | null | undefined, original: string | null | undefined): string {
  if (apiId === WORLD_CUP_API_LEAGUE_ID) return WORLD_CUP_OFFICIAL_LOGO;
  return original || "";
}

const SG7_LEAGUE_IDS = LEAGUES_CONFIG.map((l) => l.id);


export interface FallbackNextMatch {
  fixture_id: number;
  home_team: string;
  away_team: string;
  home_logo: string;
  away_logo: string;
  match_date: string;
  league_name: string;
  league_logo: string;
}

export interface FallbackInsightStats {
  teamsAlerted: number;
  teamsScored: number;
  successRate: number;
}

export interface FallbackWeekLeague {
  name: string;
  logo: string;
}

export interface FallbackWeekGame {
  day: string; // "Sunday", "Monday", etc.
  leagues: FallbackWeekLeague[];
}

export interface TickerFallbackData {
  nextMatch: FallbackNextMatch | null;
  insightStats: FallbackInsightStats | null;
  weekGames: FallbackWeekGame[];
  gapHours: number | null;
}

async function fetchFallbackData(nextFixtureDate: string | null): Promise<TickerFallbackData> {
  const now = new Date();
  const sevenDaysAhead = addDays(now, 7).toISOString();

  // 1. Next big match (first scheduled game with team info)
  const { data: nextGames } = await supabase
    .from("games")
    .select(`
      fixture_id, match_date,
      home_team:teams!games_home_team_id_fkey(name, logo_url),
      away_team:teams!games_away_team_id_fkey(name, logo_url),
      league:leagues!games_league_id_fkey(name, api_football_id, logo_url)
    `)
    .eq("status", "scheduled")
    .gte("match_date", now.toISOString())
    .lte("match_date", sevenDaysAhead)
    .order("match_date", { ascending: true })
    .limit(10);

  // Filter to SG7 leagues and pick first
  const wcMode = isWorldCupWindow(now);
  const sg7Games = (nextGames || []).filter((g: any) => {
    const apiId = g.league?.api_football_id;
    if (!apiId) return false;
    return wcMode ? apiId === WORLD_CUP_API_LEAGUE_ID : SG7_LEAGUE_IDS.includes(apiId);
  });


  const topMatch = sg7Games[0] as any;
  const nextMatch: FallbackNextMatch | null = topMatch
    ? {
        fixture_id: topMatch.fixture_id,
        home_team: topMatch.home_team?.name || "TBD",
        away_team: topMatch.away_team?.name || "TBD",
        home_logo: topMatch.home_team?.logo_url || "",
        away_logo: topMatch.away_team?.logo_url || "",
        match_date: topMatch.match_date,
        league_name: topMatch.league?.name || "",
        league_logo: resolveLeagueLogo(topMatch.league?.api_football_id, topMatch.league?.logo_url),
      }
    : null;

  // 2. Insight stats from the LAST matchday only
  const sevenDaysAgo = addDays(now, -7).toISOString();
  const { data: locks } = await supabase
    .from("sync_control" as any)
    .select("id, updated_at")
    .or("id.like.insight-push-%,id.like.insight-confirm-%")
    .gte("updated_at", sevenDaysAgo);

  let teamsAlerted = 0;
  let teamsScored = 0;

  if (locks && locks.length > 0) {
    const allLocks = locks as unknown as { id: string; updated_at: string }[];

    // Find latest matchday from push locks
    const pushLocksAll = allLocks.filter(
      (l) => l.id.startsWith("insight-push-") && !l.id.includes("-count-")
    );

    let maxDate: Date | null = null;
    for (const l of pushLocksAll) {
      const d = startOfDay(new Date(l.updated_at));
      if (!maxDate || d.getTime() > maxDate.getTime()) maxDate = d;
    }

    if (maxDate) {
      const dayStart = startOfDay(maxDate);
      const dayEnd = endOfDay(maxDate);

      // Filter locks to that single day
      const isOnDay = (ua: string) => {
        const t = new Date(ua).getTime();
        return t >= dayStart.getTime() && t <= dayEnd.getTime();
      };

      const pushLocks = pushLocksAll.filter((l) => isOnDay(l.updated_at));
      const confirmLocks = allLocks
        .filter((l) => l.id.startsWith("insight-confirm-"))
        .filter((l) => isOnDay(l.updated_at));

      // Unique fixture+team pairs from push locks
      const alertedPairs = new Set<string>();
      for (const l of pushLocks) {
        const stripped = l.id.replace("insight-push-", "");
        const parts = stripped.split("-");
        if (parts.length < 3) continue;
        const fixtureId = parts[0];
        const teamId = parts.slice(1, -1).join("-");
        alertedPairs.add(`${fixtureId}-${teamId}`);
      }
      teamsAlerted = alertedPairs.size;

      // Unique fixture+team pairs from confirm locks
      const confirmedPairs = new Set<string>();
      for (const l of confirmLocks) {
        const stripped = l.id.replace("insight-confirm-", "");
        const parts = stripped.split("-");
        if (parts.length < 2) continue;
        const fixtureId = parts[0];
        const teamId = parts.slice(1).join("-");
        confirmedPairs.add(`${fixtureId}-${teamId}`);
      }
      // Only count confirmed teams that were also alerted
      for (const pair of confirmedPairs) {
        if (alertedPairs.has(pair)) teamsScored++;
      }

      // Late-scored detection: check game scores for alerted teams without confirm locks
      const alertedFixtureIds = new Set<number>();
      const alertedByFixture = new Map<number, Set<string>>();
      for (const pair of alertedPairs) {
        const dashIdx = pair.indexOf("-");
        const fixtureId = parseInt(pair.substring(0, dashIdx));
        const teamId = pair.substring(dashIdx + 1);
        alertedFixtureIds.add(fixtureId);
        if (!alertedByFixture.has(fixtureId)) alertedByFixture.set(fixtureId, new Set());
        alertedByFixture.get(fixtureId)!.add(teamId);
      }

      if (alertedFixtureIds.size > 0) {
        const fixtureArray = Array.from(alertedFixtureIds);
        const [{ data: scoresData }, { data: teamIdData }] = await Promise.all([
          supabase
            .from("v_games_min")
            .select("fixture_id, home_score, away_score")
            .in("fixture_id", fixtureArray),
          supabase
            .from("games")
            .select("fixture_id, home_team_id, away_team_id")
            .in("fixture_id", fixtureArray),
        ]);

        const scoreMap = new Map<number, { homeScore: number | null; awayScore: number | null }>();
        (scoresData ?? []).forEach((g: any) => {
          scoreMap.set(g.fixture_id, { homeScore: g.home_score, awayScore: g.away_score });
        });
        const teamMap = new Map<number, { homeTeamId: string | null; awayTeamId: string | null }>();
        (teamIdData ?? []).forEach((g: any) => {
          teamMap.set(g.fixture_id, { homeTeamId: g.home_team_id, awayTeamId: g.away_team_id });
        });

        for (const [fixtureId, teamIds] of alertedByFixture) {
          const scores = scoreMap.get(fixtureId);
          const teams = teamMap.get(fixtureId);
          if (!scores || !teams) continue;
          for (const tid of teamIds) {
            if (confirmedPairs.has(`${fixtureId}-${tid}`)) continue;
            const isHome = tid === teams.homeTeamId;
            const isAway = tid === teams.awayTeamId;
            if (isHome && (scores.homeScore ?? 0) > 0) teamsScored++;
            else if (isAway && (scores.awayScore ?? 0) > 0) teamsScored++;
          }
        }
      }
    }
  }

  const successRate = teamsAlerted > 0 ? Math.round((teamsScored / teamsAlerted) * 100) : 0;
  const insightStats: FallbackInsightStats | null =
    teamsAlerted > 0 ? { teamsAlerted, teamsScored, successRate } : null;

  // 3. Week games — distinct leagues per day (apply WC logo override)
  const weekGamesMap = new Map<string, Map<string, string>>();
  for (const g of sg7Games) {
    const gameAny = g as any;
    const day = format(new Date(gameAny.match_date), "EEEE");
    if (!weekGamesMap.has(day)) weekGamesMap.set(day, new Map());
    if (gameAny.league?.name) {
      const logo = resolveLeagueLogo(gameAny.league.api_football_id, gameAny.league.logo_url);
      weekGamesMap.get(day)!.set(gameAny.league.name, logo);
    }
  }
  const weekGames: FallbackWeekGame[] = Array.from(weekGamesMap.entries()).map(([day, leagueMap]) => ({
    day,
    leagues: Array.from(leagueMap.entries()).map(([name, logo]) => ({ name, logo })),
  }));

  // Gap hours
  const gapHours = nextFixtureDate
    ? Math.round((new Date(nextFixtureDate).getTime() - now.getTime()) / (1000 * 60 * 60))
    : null;

  return { nextMatch, insightStats, weekGames, gapHours };
}

export function useTickerFallbackData(enabled: boolean, nextFixtureDate: string | null) {
  return useQuery({
    queryKey: ["ticker-fallback", nextFixtureDate],
    queryFn: () => fetchFallbackData(nextFixtureDate),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}
